
import React, { useState, useEffect } from "react";
import { Control, UseFormWatch, UseFormSetValue } from "react-hook-form";
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { X, Plus, Code, Play, RefreshCw, FileDown, Database } from "lucide-react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { getAvailableQueries, executeWidgetQuery, QueryDefinition } from "@/components/admin/dashboard/queries";
import { useToast } from "@/hooks/use-toast";
import { DashboardType } from "@/components/admin/dashboard/widget-types";

interface QueryBuilderFormProps {
  control: Control<any>;
  setValue: UseFormSetValue<any>;
  watch: UseFormWatch<any>;
}

export function QueryBuilderForm({ control, setValue, watch }: QueryBuilderFormProps) {
  const [newField, setNewField] = useState("");
  const queryName = watch("queryName");
  const dataFields = watch("dataFields") || [];
  const dashboardType = watch("dashboardType") as DashboardType;
  const [queryPreview, setQueryPreview] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [tabValue, setTabValue] = useState("existing");
  const [availableQueries, setAvailableQueries] = useState<QueryDefinition[]>([]);
  const { toast } = useToast();

  // Fetch available queries when component mounts or dashboard type changes
  useEffect(() => {
    const queries = getAvailableQueries();
    setAvailableQueries(queries);
  }, [dashboardType]);

  const handleSampleQuerySelect = (query: QueryDefinition) => {
    setValue("queryName", query.name);
    // We don't set dataFields here anymore - the user should select the fields they want
    // from the query result after testing
  };

  const handleAddField = () => {
    if (newField.trim() && !dataFields.includes(newField.trim())) {
      setValue("dataFields", [...dataFields, newField.trim()]);
      setNewField("");
    }
  };

  const handleRemoveField = (field: string) => {
    setValue("dataFields", dataFields.filter((f: string) => f !== field));
  };

  const handleTestQuery = async () => {
    if (!queryName) {
      toast({
        title: "Query name required",
        description: "Please enter or select a query name to test",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);
    try {
      const result = await executeWidgetQuery(queryName);
      
      if (result.error) {
        toast({
          title: "Query Error",
          description: `Error executing query: ${result.error.message}`,
          variant: "destructive",
        });
        setQueryPreview(JSON.stringify({ error: result.error.message }, null, 2));
      } else {
        setQueryPreview(JSON.stringify(result.data, null, 2));
        
        // If we have data and no dataFields selected yet, suggest them
        if (result.data && (!dataFields || dataFields.length === 0)) {
          const suggestedFields = Object.keys(result.data);
          if (suggestedFields.length > 0) {
            toast({
              title: "Fields detected",
              description: "Available data fields have been detected from the query result",
            });
          }
        }
      }
    } catch (error: any) {
      toast({
        title: "Query Error",
        description: `Error executing query: ${error.message}`,
        variant: "destructive",
      });
      setQueryPreview(JSON.stringify({ error: error.message }, null, 2));
    } finally {
      setIsLoading(false);
    }
  };

  const extractDataFields = () => {
    if (!queryPreview) return;
    
    try {
      const data = JSON.parse(queryPreview);
      const fields = Object.keys(data);
      setValue("dataFields", fields);
      
      toast({
        title: "Fields Extracted",
        description: `${fields.length} field(s) extracted from the query result`,
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Could not extract fields from the query preview",
        variant: "destructive",
      });
    }
  };

  const getDashboardTypeLabel = (types: DashboardType[]): string => {
    if (types.includes('both')) return "Both";
    if (types.includes('admin') && types.includes('user')) return "Both";
    if (types.includes('admin')) return "Admin";
    if (types.includes('user')) return "User";
    return "Unknown";
  };

  return (
    <div className="space-y-6">
      <Tabs value={tabValue} onValueChange={setTabValue} className="w-full">
        <TabsList className="mb-4">
          <TabsTrigger value="existing">Existing Queries</TabsTrigger>
          <TabsTrigger value="custom">Custom Query</TabsTrigger>
        </TabsList>
        
        <TabsContent value="existing" className="space-y-4">
          <div className="grid grid-cols-1 gap-4">
            <div>
              <FormLabel>Available Database Queries</FormLabel>
              <p className="text-sm text-muted-foreground mb-2">
                Showing queries compatible with <span className="font-medium">
                  {dashboardType === 'admin' ? 'Admin Dashboard' : 
                   dashboardType === 'user' ? 'User Dashboard' : 'Both Dashboards'}
                </span>
              </p>
              <ScrollArea className="h-[200px] border rounded-md p-2">
                <div className="space-y-2">
                  {availableQueries.length > 0 ? (
                    availableQueries.map((query) => (
                      <Card
                        key={query.name}
                        className="p-2 cursor-pointer hover:bg-accent flex justify-between items-center"
                        onClick={() => handleSampleQuerySelect(query)}
                      >
                        <div>
                          <div className="font-medium flex items-center gap-2">
                            <Database className="h-4 w-4" />
                            {query.name}
                          </div>
                          <div className="text-xs text-muted-foreground">{query.description}</div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="text-xs">
                            {getDashboardTypeLabel(query.dashboardTypes)}
                          </Badge>
                          <Button 
                            size="sm" 
                            variant={queryName === query.name ? "default" : "ghost"}
                            onClick={() => handleSampleQuerySelect(query)}
                          >
                            {queryName === query.name ? "Selected" : "Use"}
                          </Button>
                        </div>
                      </Card>
                    ))
                  ) : (
                    <div className="p-4 text-center text-muted-foreground">
                      No queries available for the selected dashboard type
                    </div>
                  )}
                </div>
              </ScrollArea>
              <FormDescription className="mt-2">
                Select from existing database queries available for widgets
              </FormDescription>
            </div>
          </div>
        </TabsContent>
        
        <TabsContent value="custom" className="space-y-4">
          <FormField
            control={control}
            name="queryName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Custom Query Name</FormLabel>
                <FormControl>
                  <Input placeholder="e.g., getCustomStats" {...field} />
                </FormControl>
                <FormDescription>
                  Enter a unique name for your custom query function
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
          
          <Alert className="bg-amber-50">
            <AlertDescription>
              Custom queries need to be implemented by a developer in the database functions. Save this widget definition with the custom query name, and coordinate with your developer to implement the actual function.
            </AlertDescription>
          </Alert>
        </TabsContent>
      </Tabs>

      <div>
        <div className="flex justify-between items-center mb-2">
          <FormLabel>Query Preview</FormLabel>
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleTestQuery}
              disabled={!queryName || isLoading}
            >
              {isLoading ? (
                <RefreshCw className="h-3 w-3 mr-1 animate-spin" />
              ) : (
                <Play className="h-3 w-3 mr-1" />
              )}
              Test Query
            </Button>
            
            {queryPreview && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={extractDataFields}
              >
                <FileDown className="h-3 w-3 mr-1" />
                Extract Fields
              </Button>
            )}
          </div>
        </div>
        
        <div className="relative">
          <Textarea
            className="font-mono text-sm h-[200px] bg-muted"
            readOnly
            value={queryPreview || "// Click 'Test Query' to see a preview of the data"}
          />
          <Code className="absolute top-2 right-2 h-5 w-5 text-muted-foreground" />
        </div>
        <FormDescription>
          This shows real data from the database based on the selected query
        </FormDescription>
      </div>

      <div>
        <FormLabel>Data Fields</FormLabel>
        <FormDescription className="mb-2">
          Specify which fields from the query result to use in the widget
        </FormDescription>
        
        <div className="flex flex-wrap gap-2 mb-4">
          {dataFields.map((field: string) => (
            <Badge key={field} variant="secondary" className="p-2">
              {field}
              <X
                className="ml-1 h-3 w-3 cursor-pointer"
                onClick={() => handleRemoveField(field)}
              />
            </Badge>
          ))}
        </div>
        
        <div className="flex gap-2">
          <Input
            placeholder="Add a field name"
            value={newField}
            onChange={(e) => setNewField(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleAddField()}
          />
          <Button
            type="button"
            variant="outline"
            onClick={handleAddField}
            disabled={!newField.trim()}
          >
            <Plus className="h-4 w-4 mr-1" /> Add
          </Button>
        </div>
      </div>
    </div>
  );
}
